import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Read-only Stripe payout summary card for the host Dashboard.
 *
 * Visibility policy (Iter123):
 *   - Host NOT connected → render NOTHING on Dashboard. The Connect Stripe
 *     CTA lives on /settings/payments, not the Dashboard.
 *   - Host connected + balance loads → show Available + Pending.
 *   - Host connected + Stripe API failure → show red error card (actionable).
 *   - Frontend fetch itself fails (network, env missing, etc.) → render NOTHING
 *     because we cannot prove the host is connected. We never fabricate a
 *     red error for an unknown-status host.
 */
export default function StripePayoutSummaryCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tok = localStorage.getItem('session_token');
        const res = await axios.get(`${API}/stripe-connect/balance`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        // Do NOT fabricate connected:true — we don't actually know whether
        // this host has Stripe. Surfacing a red error card here causes false
        // positives for hosts who only use cash/Venmo/Zelle.
        if (!cancelled) setData({ connected: null, fetchFailed: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatMoney = (cents, currency) => {
    const v = (cents || 0) / 100;
    try {
      return v.toLocaleString(undefined, {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: 2,
      });
    } catch {
      return `$${v.toFixed(2)}`;
    }
  };

  // Render NOTHING during loading — avoids a flash of a "Stripe Payouts" card
  // for hosts who don't even use Stripe. The component appears only once we
  // confirm the host is connected.
  if (loading) return null;

  // Not connected, unknown status (fetch failure), or any other non-connected
  // shape → render nothing. The Connect Stripe CTA lives on /settings/payments.
  if (!data || data.connected !== true) return null;

  // Error state — only when backend explicitly confirms the host IS connected
  // but Stripe Balance.retrieve failed. This is genuinely actionable.
  if (data.error) {
    return (
      <div className="bg-[#0A0A0A] border border-[#5f1f1f] rounded-sm p-5 mb-8" data-testid="stripe-payout-card-error">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#221111] border border-[#5f1f1f] flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-[#F87171]" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-[#777] font-semibold" data-testid="stripe-payout-card-title">Stripe Payouts</div>
            <p className="text-sm text-[#F87171] mt-1 leading-snug" data-testid="stripe-payout-card-error-text">{data.error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Connected → balance state
  const available = formatMoney(data?.available_cents, data?.currency);
  const pending = formatMoney(data?.pending_cents, data?.currency);
  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-5 mb-8" data-testid="stripe-payout-card-connected">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-sm bg-[#0f231a] border border-[#1f5f44] flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-4 h-4 text-[#6EE7B7]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-[#777] font-semibold" data-testid="stripe-payout-card-title">Stripe Payouts</div>
          <p className="text-[10px] text-[#555] mt-0.5">Read-only — manage payouts in your Stripe dashboard</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-sm border border-[#1A1A1A] bg-[#050505] p-3" data-testid="stripe-payout-available">
          <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Available</div>
          <div className="text-xl font-bold text-[#6EE7B7] mt-1 truncate" data-testid="stripe-payout-available-value">{available}</div>
        </div>
        <div className="rounded-sm border border-[#1A1A1A] bg-[#050505] p-3" data-testid="stripe-payout-pending">
          <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Pending</div>
          <div className="text-xl font-bold text-[#D4AF37] mt-1 truncate" data-testid="stripe-payout-pending-value">{pending}</div>
        </div>
      </div>
      <p className="text-[10px] text-[#555] mt-2 uppercase tracking-wider" data-testid="stripe-payout-currency">{(data?.currency || 'usd').toUpperCase()}</p>
    </div>
  );
}
